// =====================================================================
// primeiro15-perguntas.js — FONTE ÚNICA (frontend) da estrutura do
// "Primeiros 15 dias" reformulado (padrão pontuado, nota híbrida).
// Usada pelo questionário externo (renderiza) e pelo relatório (rótulos).
//
// ESPELHA server/rh/primeiro15-perguntas.js (backend). IDs P_N, seções,
// tipos e polaridade PRECISAM ficar iguais nos dois arquivos. Ao mexer num,
// mexer no outro (mesma limitação CRA/ESM × CommonJS da Clima/Desligamento).
//
// tipo: 'simnao' | 'escala' (1-5) | 'selecao' | 'nps' (0-10) | 'texto'
// Campo opcional `nota`: aviso curto exibido sob o enunciado da pergunta
// (usado no P_22 para não criar expectativa de troca de escala).
// =====================================================================

// Escala 1-5: só as pontas rotuladas na UI.
export const ESCALA_MIN = 1;
export const ESCALA_MAX = 5;
export const ESCALA_ROTULO_MIN = 'Muito baixa';
export const ESCALA_ROTULO_MAX = 'Muito alta';

export const SECOES = [
  {
    id: 1, titulo: 'Integração (Onboarding)', pontua: true,
    perguntas: [
      { id: 'P_1', tipo: 'simnao', texto: 'Você participou da aula de orientação?', positivo: 'Sim' },
      { id: 'P_2', tipo: 'selecao', texto: 'Quem te recebeu no seu primeiro dia de trabalho?',
        opcoes: ['Anfitrião(ã)', 'Atendente', 'Gerente de Área', 'Gerente de Negócios', 'Gerente de Plantão', 'Treinador', 'Ninguém'] },
      { id: 'P_3', tipo: 'escala', texto: 'Você foi bem recebido no restaurante?' },
      { id: 'P_4', tipo: 'simnao', texto: 'Você sentiu que seus primeiros dias foram bem organizados e planejados?', positivo: 'Sim' },
    ],
  },
  {
    id: 2, titulo: 'Documentação e Benefícios', pontua: true,
    perguntas: [
      { id: 'P_5', tipo: 'simnao', texto: 'Você recebeu o seu kit de uniforme completo?', positivo: 'Sim' },
      { id: 'P_6', tipo: 'simnao', texto: 'Você está recebendo o vale-transporte conforme solicitado?', positivo: 'Sim' },
      { id: 'P_7', tipo: 'simnao', texto: 'Você tem alguma dúvida sobre o seu salário?', positivo: 'Não' },
      { id: 'P_8', tipo: 'simnao', texto: 'Você entendeu sua escala de folga?', positivo: 'Sim' },
      // --- Preferência de escala (P_20..P_22, 2026-08-03) ---
      // Moram AQUI, e não numa seção própria, de propósito: a seção já trata de
      // escala (P_8) e o encadeamento fica natural (entendeu sua escala → qual
      // é ela → qual seu turno → qual seria a melhor). Uma seção "Escala de
      // Trabalho" daria holofote justamente à pergunta que precisa passar sem
      // alarde. IDs fora de ordem são normais: o P_N é chave de coluna, a
      // numeração que o colaborador vê é a posição dentro da seção.
      { id: 'P_20', tipo: 'selecao', texto: 'Qual é a sua escala de trabalho hoje?',
        opcoes: ['5x2', '12x36', 'Não sei informar'] },
      { id: 'P_21', tipo: 'selecao', texto: 'Qual é o seu turno hoje?',
        opcoes: ['Diurno', 'Noturno', 'Não sei informar'] },
      // ⚠️ O aviso da `nota` é o CORAÇÃO da demanda, não enfeite: sem ele o
      // colaborador lê "prefere" e entende "posso pedir troca", e a pesquisa
      // vira promessa não cumprida — o oposto do que o P15 existe para fazer.
      // Foi descartada a formulação "segundo a legislação": ninguém lê aquilo
      // como enquadramento jurídico. O que neutraliza expectativa é o aviso
      // explícito, não o rodeio na redação.
      { id: 'P_22', tipo: 'selecao', texto: 'Na sua opinião, qual destes formatos de escala seria o melhor para você?',
        nota: 'Esta pergunta serve apenas para conhecermos o perfil da equipe. As escalas são definidas pela necessidade operacional de cada restaurante e não mudam a partir desta resposta.',
        opcoes: ['5x2 — diurno', '5x2 — noturno', '12x36 — diurno', '12x36 — noturno', 'Não tenho preferência'] },
    ],
  },
  {
    id: 3, titulo: 'Liderança', pontua: true,
    perguntas: [
      { id: 'P_9', tipo: 'simnao', texto: 'Você conheceu o seu Gerente de Restaurante?', positivo: 'Sim' },
      { id: 'P_10', tipo: 'escala', texto: 'Você recebeu o apoio necessário da liderança durante seus primeiros dias?' },
    ],
  },
  {
    id: 4, titulo: 'Treinamento', pontua: true,
    perguntas: [
      { id: 'P_11', tipo: 'selecao', texto: 'Como está o andamento da sua trilha de treinamento?',
        opcoes: ['Adiantada', 'Conforme planejado', 'Atrasada', 'Ainda não iniciei', 'Não conheço a minha trilha'] },
      { id: 'P_12', tipo: 'simnao', texto: 'Acessou a ferramenta de treinamento "Saber"?', positivo: 'Sim' },
      { id: 'P_13', tipo: 'simnao', texto: 'Os treinamentos foram suficientes para iniciar suas atividades?', positivo: 'Sim' },
    ],
  },
  {
    id: 5, titulo: 'Ambiente e Relacionamento', pontua: true,
    perguntas: [
      { id: 'P_14', tipo: 'escala', texto: 'Você tem um bom relacionamento com a equipe?' },
      { id: 'P_15', tipo: 'escala', texto: 'Você se sentiu respeitado no restaurante?' },
    ],
  },
  {
    id: 6, titulo: 'Engajamento', pontua: false,
    perguntas: [
      { id: 'P_16', tipo: 'selecao', texto: 'O que você mais gosta no seu trabalho?',
        opcoes: ['Benefícios', 'Equipe', 'Plano de Carreira', 'Salário', 'Nenhum dos tópicos acima'] },
      { id: 'P_17', tipo: 'selecao', texto: 'Por quanto tempo você se imagina trabalhando conosco?',
        opcoes: ['3 a 6 meses', '6 meses a 1 ano', '1 a 2 anos', '2 a 3 anos', 'Mais de 3 anos'] },
      { id: 'P_18', tipo: 'nps', texto: 'Qual é a probabilidade de recomendar a empresa para um amigo ou familiar?' },
    ],
  },
  {
    id: 7, titulo: 'Sugestões', pontua: false,
    perguntas: [
      { id: 'P_19', tipo: 'texto', texto: 'Você tem alguma sugestão para melhorar sua integração, treinamento e experiência no restaurante?' },
    ],
  },
];

// Lista achatada (ordem P_1..P_19), útil p/ relatório/CSV.
export const TODAS_PERGUNTAS = SECOES.flatMap((s) => s.perguntas);
